import type { NotesParams } from '../hooks/useApi';
import { SUBJECT_OPTIONS, UNIVERSITY_YEAR_OPTIONS } from './noteLabels';

export type NotesSort = 'newest' | 'oldest' | 'rating' | 'downloads';

export interface NotesFilters {
  q: string;
  subject: string;
  university: string;
  department: string;
  year: string;
  sort: NotesSort;
  page: number;
}

export const DEFAULT_NOTES_FILTERS: NotesFilters = {
  q: '',
  subject: '',
  university: '',
  department: '',
  year: '',
  sort: 'newest',
  page: 1,
};

const SORT_OPTIONS: NotesSort[] = ['newest', 'oldest', 'downloads', 'rating'];

function parseSort(value: string | null): NotesSort {
  if (value && SORT_OPTIONS.includes(value as NotesSort)) {
    return value as NotesSort;
  }
  return 'newest';
}

function parsePage(value: string | null): number {
  const n = parseInt(value || '1', 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

export function parseNotesFilters(searchParams: URLSearchParams): NotesFilters {
  const subject = searchParams.get('subject') || '';
  return {
    q: (searchParams.get('q') || '').trim(),
    subject: SUBJECT_OPTIONS.includes(subject) ? subject : '',
    university: (searchParams.get('university') || '').trim(),
    department: (searchParams.get('department') || '').trim(),
    year: UNIVERSITY_YEAR_OPTIONS.includes(searchParams.get('year') || '')
      ? (searchParams.get('year') as string)
      : '',
    sort: parseSort(searchParams.get('sort')),
    page: parsePage(searchParams.get('page')),
  };
}

export function filtersToSearchParams(filters: Partial<NotesFilters>): URLSearchParams {
  const params = new URLSearchParams();
  const merged = { ...DEFAULT_NOTES_FILTERS, ...filters };

  if (merged.q) params.set('q', merged.q);
  if (merged.subject) params.set('subject', merged.subject);
  if (merged.university) params.set('university', merged.university);
  if (merged.department) params.set('department', merged.department);
  if (merged.year) params.set('year', merged.year);
  if (merged.sort && merged.sort !== 'newest') params.set('sort', merged.sort);
  if (merged.page && merged.page > 1) params.set('page', String(merged.page));

  return params;
}

export function filtersToApiParams(filters: NotesFilters, limit = 12): NotesParams {
  return {
    page: filters.page,
    limit,
    sort: filters.sort,
    search: filters.q || undefined,
    subject: filters.subject || undefined,
    university: filters.university || undefined,
    department: filters.department || undefined,
    universityYear: filters.year || undefined,
  };
}

export function countActiveFilters(filters: NotesFilters): number {
  let n = 0;
  if (filters.q) n += 1;
  if (filters.subject) n += 1;
  if (filters.university) n += 1;
  if (filters.department) n += 1;
  if (filters.year) n += 1;
  if (filters.sort !== 'newest') n += 1;
  return n;
}

export function hasActiveFilters(filters: NotesFilters): boolean {
  return countActiveFilters(filters) > 0;
}

export const SORT_LABELS: Record<NotesSort, string> = {
  newest: 'En yeni',
  oldest: 'En eski',
  downloads: 'En çok indirilen',
  rating: 'En yüksek puan',
};
